import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import teamAPI from "../../../services/teamApi";
import { DOCTORS_BASE } from "../AdminDoctors/doctorConstants";
import { TEAM_BASE, TEAM_ROLE_LABELS } from "./teamConstants";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { useHubBasePath } from "../../../utils/hubPaths";
import { isClubOwner } from "../../../utils/roles";
import { usePlanAccess } from "../../../hooks/usePlanAccess";
import L from "../../../constants/baysportLabels";
import aa from "../AdminPortal/adminApp.module.css";
import md from "../common/manageDesktop.module.css";
import SwipeableRow from "../AdminPortal/SwipeableRow";
import {
  HubListGroup,
  HubNavAdd,
  HubRow,
  HubScreen,
  HubSegment,
  HubSheet,
  HubSheetActions,
  DesktopDialog,
} from "../AdminPortal/HubPwa";
import TeamInviteForm from "./TeamInviteForm";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import {
  Button,
  Input,
  Select,
  EmptyState,
  useToast,
  IconPlus,
  IconSearch,
} from "../../ui";

function roleBadgeClass(role) {
  if (role === "owner") return u.badgePurple;
  if (role === "admin") return u.badgeBlue;
  return u.badgeGreen;
}

function formatAssignments(member) {
  if (member.role === "owner") return "Владелец клуба";
  if (member.role === "admin") return "Управление клубом";
  if (member.role !== "trainer") return "—";
  const groupNames = (member.groups || []).map((g) => g.name).filter(Boolean);
  if (groupNames.length) return groupNames.join(", ");
  const branchNames = (member.branches || []).map((b) => b.name);
  if (branchNames.length) return `залы: ${branchNames.join(", ")}`;
  return "Группа не назначена";
}

function memberCanManage(member, currentUserId, viewerIsOwner) {
  if (member.id === currentUserId || member.is_owner) return false;
  if (member.role === "admin") return Boolean(viewerIsOwner);
  return true;
}

function memberStatus(member) {
  if (member.trainer_active === false || member.is_blocked) {
    return { label: "Деактивирован", tone: "danger" };
  }
  return { label: "Активен", tone: "ok" };
}

function TeamActions({
  member,
  busyId,
  currentUserId,
  viewerIsOwner,
  onProfile,
  onSchedule,
  onMessage,
  layout = "sheet",
}) {
  const isSelf = member.id === currentUserId;
  const canManage = memberCanManage(member, currentUserId, viewerIsOwner);
  const canMessage = !isSelf && typeof onMessage === "function";
  const showTrainerSchedule = member.role === "trainer" && member.trainer_id;
  const isTable = layout === "table";
  const compact = layout === "compact";

  const btnClass = compact ? aa.teamRowAction : undefined;
  const Btn = compact ? "button" : Button;
  const btnProps = compact
    ? { type: "button", className: btnClass, disabled: busyId === member.id }
    : {
        type: "button",
        size: "sm",
        variant: "secondary",
        disabled: busyId === member.id,
      };

  const items = [];

  if (canMessage) {
    items.push(
      <Btn key="message" {...btnProps} onClick={() => onMessage(member)}>
        {compact || isTable ? "Чат" : "Написать"}
      </Btn>,
    );
  }

  items.push(
    <Btn key="profile" {...btnProps} onClick={() => onProfile(member)}>
      {canManage
        ? compact || isTable
          ? "Доступ"
          : "Доступ и роль"
        : compact || isTable
          ? "Профиль"
          : "Открыть профиль"}
    </Btn>,
  );

  if (showTrainerSchedule) {
    items.push(
      <Btn
        key="schedule"
        {...btnProps}
        onClick={() => onSchedule(member.trainer_id)}
      >
        {compact || isTable ? "Расписание" : "Расписание"}
      </Btn>,
    );
  }

  if (!items.length) {
    if (compact) return null;
    return <span className={s.teamTableEmpty}>—</span>;
  }

  if (isTable) {
    return <div className={md.desktopTeamTableActions}>{items}</div>;
  }

  return compact ? (
    <div className={aa.teamRowActions}>{items}</div>
  ) : (
    <div className={aa.teamMemberSheetActions}>{items}</div>
  );
}

function TeamTableRow({
  member,
  busyId,
  currentUserId,
  viewerIsOwner,
  onProfile,
  onSchedule,
  onMessage,
}) {
  const roleLabel = TEAM_ROLE_LABELS[member.role] || member.role;
  const detail = formatAssignments(member);
  const status = memberStatus(member);
  const showProfileLink =
    (member.role === "trainer" && member.trainer_id) ||
    member.role === "admin" ||
    member.role === "owner";

  return (
    <tr className={member.is_blocked ? s.teamTableRowBlocked : undefined}>
      <td className={s.teamTableCell}>
        {showProfileLink ? (
          <button
            type="button"
            className={md.desktopTeamNameLink}
            onClick={() => onProfile(member)}
          >
            {member.full_name || "—"}
          </button>
        ) : (
          <span className={s.teamMemberName}>{member.full_name || "—"}</span>
        )}
      </td>
      <td className={s.teamTableCell}>{member.email || "—"}</td>
      <td className={s.teamTableCell}>
        <span className={`${u.badge} ${roleBadgeClass(member.role)}`}>
          {roleLabel}
        </span>
      </td>
      <td className={s.teamTableCell}>
        <span className={s.teamTableMuted}>{detail}</span>
      </td>
      <td className={s.teamTableCell}>
        <span className={`${u.badge} ${status.tone === "ok" ? u.badgeGreen : u.badgeRed}`}>
          {status.label}
        </span>
      </td>
      <td className={`${s.teamTableCell} ${md.desktopTeamTableCellActions}`}>
        <TeamActions
          member={member}
          busyId={busyId}
          currentUserId={currentUserId}
          viewerIsOwner={viewerIsOwner}
          onProfile={onProfile}
          onSchedule={onSchedule}
          onMessage={onMessage}
          layout="table"
        />
      </td>
    </tr>
  );
}

export default function AdminTeamList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const isApp = useAdminHubUi();
  const hubBase = useHubBasePath();
  const auth = useSelector((state) => state.auth);
  const currentUserId = auth.profile?.id;
  const plan = usePlanAccess();
  const viewerIsOwner = isClubOwner({
    role: auth.role,
    profileRole: auth.profile?.role,
    club: auth.club,
  });
  const canInviteAdmin = plan.adminRoles && viewerIsOwner;
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [members, setMembers] = useState([]);
  const [showInviteSheet, setShowInviteSheet] = useState(
    () => searchParams.get("invite") === "1",
  );
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get("invite") !== "1") return;
    setShowInviteSheet(true);
    const next = new URLSearchParams(searchParams);
    next.delete("invite");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(() => {
    teamAPI
      .list()
      .then((r) => setMembers(r.members || []))
      .catch((e) =>
        toast.error(e.response?.data?.error || "Не удалось загрузить команду"),
      );
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    let list = members;
    if (roleFilter === "trainer") {
      list = list.filter((m) => m.role === "trainer");
    } else if (roleFilter === "admin") {
      list = list.filter((m) => m.role === "admin" || m.role === "owner");
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(
      (m) =>
        (m.full_name || "").toLowerCase().includes(needle) ||
        (m.email || "").toLowerCase().includes(needle),
    );
  }, [members, q, roleFilter]);

  const openProfile = (memberOrTrainerId) => {
    if (memberOrTrainerId && typeof memberOrTrainerId === "object") {
      navigate(`${TEAM_BASE}/members/${memberOrTrainerId.id}`);
      return;
    }
    navigate(`${DOCTORS_BASE}/${memberOrTrainerId}`);
  };

  const openSchedule = (trainerId) => {
    navigate(`/manage/app/schedule?trainer=${trainerId}&view=week`);
  };

  const openMessage = (member) => {
    if (!member?.id || member.id === currentUserId) return;
    navigate(`${hubBase}/chat/member/${member.id}`, {
      state: { name: member.full_name || undefined },
    });
  };

  const openMember = (member) => {
    openProfile(member);
  };

  const openMemberAccess = (member) => {
    openProfile(member);
  };

  const openInviteSheet = () => setShowInviteSheet(true);
  const closeInviteSheet = () => setShowInviteSheet(false);

  const handleInviteSuccess = (res) => {
    closeInviteSheet();
    load();
    const tid = res?.member?.trainer_id;
    if (tid) {
      navigate(`${DOCTORS_BASE}/${tid}`);
    }
  };

  const inviteSheetFooter = isApp ? (
    <HubSheetActions
      primary={inviteLoading ? "Отправка…" : "Пригласить"}
      primaryForm="team-invite-form"
      primaryDisabled={inviteLoading}
      onSecondary={closeInviteSheet}
    />
  ) : (
    <>
      <Button type="button" variant="secondary" onClick={closeInviteSheet}>
        Отмена
      </Button>
      <Button type="submit" form="team-invite-form" disabled={inviteLoading}>
        {inviteLoading ? "Отправка…" : "Пригласить"}
      </Button>
    </>
  );

  const renderInviteModal = () =>
    isApp ? (
      <HubSheet
        open={showInviteSheet}
        onClose={closeInviteSheet}
        title="Пригласить в команду"
        subtitle={
          canInviteAdmin
            ? "Тренер или администратор — роль можно выбрать ниже"
            : "Пригласите тренера в команду клуба"
        }
        titleId="team-invite-title"
        footer={inviteSheetFooter}
        bodyClassName={aa.teamInviteSheetBody}
      >
        <TeamInviteForm
          sheet
          formId="team-invite-form"
          onLoadingChange={setInviteLoading}
          onSuccess={handleInviteSuccess}
          onCancel={closeInviteSheet}
        />
      </HubSheet>
    ) : (
      <DesktopDialog
        open={showInviteSheet}
        onClose={closeInviteSheet}
        title="Пригласить в команду"
        subtitle={
          canInviteAdmin
            ? "Выберите роль: тренер или администратор. Группы для тренера можно назначить позже."
            : "Пригласите тренера. Группы можно назначить позже."
        }
        titleId="team-invite-title"
        footer={inviteSheetFooter}
        wide
        bodyClassName={md.desktopTeamInviteDialogBody}
        dialogClassName={md.desktopTeamInviteDialog}
      >
        <TeamInviteForm
          sheet
          formId="team-invite-form"
          onLoadingChange={setInviteLoading}
          onSuccess={handleInviteSuccess}
        />
      </DesktopDialog>
    );

  const roleTabs = [
    { id: "", label: "Все" },
    { id: "admin", label: "Админы" },
    { id: "trainer", label: "Тренеры" },
  ];

  if (isApp) {
    return (
      <HubScreen
        title={L.team}
        right={<HubNavAdd onClick={openInviteSheet} ariaLabel="Пригласить" />}
      >
        <div className={aa.clientsSearchRow}>
          <label className={aa.clientsSearch}>
            <IconSearch className={aa.clientsSearchIcon} width={18} height={18} />
            <Input
              className={aa.clientsSearchInput}
              placeholder="ФИО или email"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск сотрудников"
            />
          </label>
        </div>

        <HubSegment tabs={roleTabs} active={roleFilter} onChange={setRoleFilter} />

        {filtered.length > 0 ? (
          <p className={aa.hubCount}>{filtered.length} сотрудников</p>
        ) : null}

        {filtered.length === 0 ? (
          <EmptyState
            title={members.length ? "Никого не нашли" : "Пока только вы"}
            description={
              members.length
                ? "Смените фильтр или поиск"
                : canInviteAdmin
                  ? "Пригласите тренера или администратора"
                  : "Пригласите тренера"
            }
            actionLabel={members.length ? undefined : "Пригласить"}
            onAction={members.length ? undefined : openInviteSheet}
          />
        ) : (
          <HubListGroup>
            {filtered.map((m) => {
              const status = memberStatus(m);
              const roleLabel = TEAM_ROLE_LABELS[m.role] || m.role;
              const detail = formatAssignments(m);
              const canManage = memberCanManage(m, currentUserId, viewerIsOwner);
              const row = (
                <HubRow
                  title={m.full_name || m.email || "—"}
                  meta={`${roleLabel}${detail !== "—" ? ` · ${detail}` : ""}`}
                  sub={m.email || undefined}
                  badge={status.label}
                  badgeTone={status.tone}
                  onClick={() => openMember(m)}
                />
              );

              if (!canManage) {
                return <div key={m.id}>{row}</div>;
              }

              return (
                <SwipeableRow
                  key={m.id}
                  actions={[
                    {
                      id: "access",
                      label: "Доступ",
                      tone: "neutral",
                      onClick: () => openMemberAccess(m),
                    },
                  ]}
                >
                  {row}
                </SwipeableRow>
              );
            })}
          </HubListGroup>
        )}
        {renderInviteModal()}
      </HubScreen>
    );
  }

  return (
    <div className={`${md.desktopHubPage} ${md.desktopTeamPage}`}>
      <header className={md.desktopHubPageHead}>
        <div className={md.desktopHubPageIntro}>
          <p className={md.desktopHubPageLead}>
            Приглашайте тренеров и назначайте группы. Тренерам доступен отдельный
            портал для журнала и расписания.
          </p>
          <p className={md.desktopHubPageMeta}>
            {filtered.length}{" "}
            {filtered.length === 1
              ? "сотрудник"
              : filtered.length < 5
                ? "сотрудника"
                : "сотрудников"}
          </p>
        </div>
        <Button size="sm" onClick={openInviteSheet}>
          <IconPlus width={16} height={16} />
          Пригласить
        </Button>
      </header>

      <div className={md.desktopHubPanel}>
        <div className={md.desktopTeamToolbar}>
          <label className={md.desktopTeamSearch}>
            <IconSearch className={md.desktopTeamSearchIcon} width={18} height={18} />
            <Input
              className={md.desktopTeamSearchInput}
              placeholder="ФИО или email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Поиск сотрудников"
            />
          </label>
          <Select
            className={md.desktopTeamRoleSelect}
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            aria-label="Фильтр по роли"
          >
            <option value="">Все роли</option>
            <option value="admin">Администраторы</option>
            <option value="trainer">Тренеры</option>
          </Select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState
            title={members.length ? "Никого не нашли" : "Пока только вы"}
            description={
              members.length
                ? "Смените фильтр или поиск"
                : "Пригласите первого тренера"
            }
            actionLabel={members.length ? undefined : "Пригласить"}
            onAction={members.length ? undefined : openInviteSheet}
          />
        ) : (
          <div className={`${s.teamTableWrap} ${md.desktopTeamTableWrap}`}>
            <table className={`${s.teamTable} ${md.desktopTeamTable}`}>
              <colgroup>
                <col />
                <col />
                <col />
                <col />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th>Email</th>
                  <th>Роль</th>
                  <th>Группы / залы</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <TeamTableRow
                    key={m.id}
                    member={m}
                    busyId={null}
                    currentUserId={currentUserId}
                    viewerIsOwner={viewerIsOwner}
                    onProfile={openProfile}
                    onSchedule={openSchedule}
                    onMessage={openMessage}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {renderInviteModal()}
    </div>
  );
}
